import type { CarFromLicensePlateRecord, CarTireTreadDepthLatestResponseRecord } from '@navio/nsdk';
import { TireSeason } from '@navio/nsdk';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { CustomDateFormats, formatDecimal, noddiFormatDate } from 'noddi-util';
import {
  CarImage,
  NavioLicensePlate,
  NavioTireSeasonChip,
  WheelSetStatus,
  wheelSetStatusToTranslation
} from 'noddi-web';
import type { ReactNode } from 'react';
import { getCommonTranslations } from '../../../utils/commonTranslations';
import { CarTireSeason, getSeasonTreadDepth } from './seasonTreadDepth';

// Past this a registry name without a family ("Mini Countryman cooper se all4") wraps to three lines at full size.
const LONG_TITLE_LENGTH = 20;

type Props = {
  car: CarFromLicensePlateRecord;
  title: string;
  latestTreadDepth: CarTireTreadDepthLatestResponseRecord | undefined;
  /** A season tile opens the tires section on that season. */
  onSeasonClick: (season: CarTireSeason) => void;
  /** The EU check tile opens the section that holds the EU control. */
  onEuControlClick: () => void;
};

export const CarHero = ({ car, title, latestTreadDepth, onSeasonClick, onEuControlClick }: Props) => {
  const translations = getCommonTranslations();
  // Only the studio photo carries empty backdrop to tuck under; a logo or nothing keeps normal spacing.
  const hasPhoto = !!car.image;

  return (
    <div className={cn('flex flex-col items-center', !hasPhoto && 'gap-6')}>
      <div className='relative z-10 flex w-full flex-col items-center gap-4 pt-2 text-center'>
        <h1
          className={cn(
            'leading-none font-bold tracking-tight text-balance break-words text-primary',
            title.length > LONG_TITLE_LENGTH ? 'text-4xl md:text-6xl' : 'text-5xl md:text-7xl'
          )}
        >
          {title}
        </h1>
        <NavioLicensePlate car={car} size='md' />
      </div>
      {/* The studio photo has plenty of backdrop above and below the car, faded out by the hero mask, so the image
          tucks under the title and the tiles to close that empty band. */}
      <CarImage car={car} alt={title} size='hero' className={cn(hasPhoto && 'md:-mt-12')} />
      <div className={cn('relative z-10 grid w-full grid-cols-1 gap-3 sm:grid-cols-3', hasPhoto && '-mt-4 md:-mt-14')}>
        <SeasonSummaryTile season={TireSeason.SUMMER} latestTreadDepth={latestTreadDepth} onClick={onSeasonClick} />
        <SeasonSummaryTile season={TireSeason.WINTER} latestTreadDepth={latestTreadDepth} onClick={onSeasonClick} />
        {car.euControlNext && (
          <SummaryTile
            icon={
              <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-lightPurple text-primary'>
                <NoddiIcon name='ShieldCheck' />
              </span>
            }
            label={translations.carDetails.euCheckDetails}
            // A bare date read as a booked EU check; the registry date is only the deadline.
            value={translations.carDetails.dueBy(
              noddiFormatDate(car.euControlNext, CustomDateFormats.SHORT_DAY_SHORT_MONTH_YEAR)
            )}
            onClick={onEuControlClick}
          />
        )}
      </div>
    </div>
  );
};

type SeasonSummaryTileProps = {
  season: CarTireSeason;
  latestTreadDepth: CarTireTreadDepthLatestResponseRecord | undefined;
  onClick: (season: CarTireSeason) => void;
};

const SeasonSummaryTile = ({ season, latestTreadDepth, onClick }: SeasonSummaryTileProps) => {
  const translations = getCommonTranslations().wheelMeasurements;
  const { status, lowestMm } = getSeasonTreadDepth(latestTreadDepth, season);

  return (
    <SummaryTile
      icon={<NavioTireSeasonChip season={season} iconOnly size='lg' translations={translations} />}
      label={season === TireSeason.SUMMER ? translations.summerTires : translations.winterTires}
      value={
        status === WheelSetStatus.MissingMeasurements || lowestMm === undefined
          ? translations.missingMeasurements
          : `${wheelSetStatusToTranslation(status, translations)} · ${formatDecimal(lowestMm, 1)} mm`
      }
      onClick={() => onClick(season)}
    />
  );
};

type SummaryTileProps = {
  icon: ReactNode;
  label: string;
  value: string;
  onClick: () => void;
};

const SummaryTile = ({ icon, label, value, onClick }: SummaryTileProps) => (
  <button
    type='button'
    onClick={onClick}
    className='flex cursor-pointer items-center gap-4 rounded-xl bg-card p-4 text-left shadow-sm transition-shadow hover:shadow-md focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none'
  >
    {icon}
    <span className='flex min-w-0 flex-col gap-0.5'>
      <span className='text-sm text-muted-foreground'>{label}</span>
      <span className='font-semibold text-primary'>{value}</span>
    </span>
  </button>
);
